import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Plus, Loader2, CalendarRange, Zap, Clock,
    X, CheckCheck, ArrowRight, BarChart3, TrendingUp, AlertCircle, Ticket, Mail,
    Sparkles, Lock, Target, Coins, CircleGauge, GraduationCap, ChartColumn, Grid2X2, List
} from 'lucide-react';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
} from 'recharts';
import CreateEventModal from '../components/CreateEventModal';
import EditEventModal from '../components/EditEventModal';
import EventCard from '../components/event/EventCard';
import TransferEventModal from '../components/event/TransferEventModal';
import QrModal from '../components/event/QrModal';
import SimpleToast from '../components/ui/SimpleToast';
import Button from '../components/ui/Button';
import { API_PATH, getToken, request } from '../config/api';
import { useAuth } from '../store/AuthContext';
import { useConfirm } from '../store/ConfirmDialogContext';
import { useToast } from '../hooks/useToast';
import { getEventStatus } from '../utils/eventHelpers';
import { exportEventToExcel } from '../utils/exportResults';
import { formatDate } from '../utils/formatDate';
import styles from './Dashboard.module.css';

const API_URL = `${API_PATH}/events`;
const APP_URL = window.location.origin;
const MIN_MONTHS_FOR_TREND_CHART = 4;
const EVENT_VIEW_STORAGE_KEY = 'livepulse-dashboard-event-view';

const getUserName = () => {
    const token = getToken();
    if (!token) return 'there';
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.name?.split(' ')[0] || 'there';
    } catch { return 'there'; }
};

const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
};

const STATUS_COLORS = { active: 'var(--success-dark)', upcoming: 'var(--warning)', ended: 'var(--text-muted)' };

/* ── Dashboard overview charts ────────────────────────────────────────────── */
const DashboardCharts = ({ events }) => {
    const { statusData, monthlyData, eventsThisMonth } = useMemo(() => {
        const now = new Date();
        let active = 0, upcoming = 0, ended = 0;
        const byMonth = {};
        events.forEach((e) => {
            const status = getEventStatus(e);
            if (status.color === 'active') active++;
            else if (status.color === 'upcoming') upcoming++;
            else ended++;
            const start = e.start_date ? new Date(e.start_date) : null;
            if (start) {
                const key = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
                byMonth[key] = (byMonth[key] || 0) + 1;
            }
        });
        const statusData = [
            { name: 'Active', value: active, color: STATUS_COLORS.active },
            { name: 'Upcoming', value: upcoming, color: STATUS_COLORS.upcoming },
            { name: 'Ended', value: ended, color: STATUS_COLORS.ended },
        ].filter((d) => d.value > 0);
        const monthlyData = Object.entries(byMonth)
            .sort(([a], [b]) => a.localeCompare(b))
            .slice(-6)
            .map(([key, count]) => ({
                month: key.slice(5) + '/' + key.slice(2, 4),
                events: count,
            }));
        const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const eventsThisMonth = byMonth[currentMonthKey] || 0;
        return { statusData, monthlyData, eventsThisMonth };
    }, [events]);

    if (statusData.length === 0 && monthlyData.length === 0) return null;

    // Fewer than 4 monthly data points reads as noise in a bar chart — a
    // plain stat communicates the same information more clearly at this size.
    const showTrendChart = monthlyData.length >= MIN_MONTHS_FOR_TREND_CHART;

    return (
        <div className={styles.chartsRow}>
            <div className={styles.chartCard}>
                <div className={styles.chartCardHeader}>
                    <BarChart3 size={18} className={styles.chartCardIcon} />
                    <span>Event status</span>
                </div>
                <div className={styles.chartCardBody}>
                    {statusData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart margin={{ top: 28, right: 28, bottom: 28, left: 28 }}>
                                <Pie
                                    data={statusData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={48}
                                    outerRadius={70}
                                    paddingAngle={2}
                                    dataKey="value"
                                    nameKey="name"
                                    label={({ name, value }) => `${name}: ${value}`}
                                >
                                    {statusData.map((entry) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(v) => [v, 'Events']} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className={styles.chartEmpty}>No event data yet</div>
                    )}
                </div>
            </div>
            <div className={styles.chartCard}>
                <div className={styles.chartCardHeader}>
                    <TrendingUp size={18} className={styles.chartCardIcon} />
                    <span>Events by month</span>
                </div>
                <div className={styles.chartCardBody}>
                    {showTrendChart ? (
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={monthlyData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                                <defs>
                                    <linearGradient id="eventsBarGradient" x1="0" y1="1" x2="0" y2="0">
                                        <stop offset="0%" stopColor="var(--cat-teal)" />
                                        <stop offset="100%" stopColor="var(--primary-500)" />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v) => [v, 'Events']} />
                                <Bar dataKey="events" fill="url(#eventsBarGradient)" radius={[4, 4, 0, 0]} name="Events" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : monthlyData.length > 0 ? (
                        <div className={styles.trendStat}>
                            <span className={styles.trendStatValue}>{eventsThisMonth}</span>
                            <span className={styles.trendStatLabel}>event{eventsThisMonth === 1 ? '' : 's'} created this month</span>
                            <span className={styles.trendStatHint}>A monthly trend chart appears once you have {MIN_MONTHS_FOR_TREND_CHART}+ months of history.</span>
                        </div>
                    ) : (
                        <div className={styles.chartEmpty}>No events with dates yet</div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AccountQuickActions = ({ user, coins, hasActiveSubscription }) => (
    <div className={styles.accountCard}>
        <div className={styles.accountCardHeader}>
            <CircleGauge size={18} className={styles.chartCardIcon} />
            <span>Account &amp; quick actions</span>
        </div>
        <div className={styles.accountCardBody}>
            <div className={styles.accountStatRow}>
                <Coins size={16} className={styles.accountStatIcon} />
                <div>
                    <div className={styles.accountStatValue}>{coins}</div>
                    <div className={styles.accountStatLabel}>Coins available</div>
                </div>
            </div>
            <div className={styles.accountStatRow}>
                <Sparkles size={16} className={styles.accountStatIcon} />
                <div>
                    <div className={styles.accountStatValue}>
                        {hasActiveSubscription ? 'Active' : 'None'}
                    </div>
                    <div className={styles.accountStatLabel}>
                        {hasActiveSubscription
                            ? `Subscription · unlimited polls till ${formatDate(user.subscriptionEndDate)}`
                            : 'No active subscription'}
                    </div>
                </div>
            </div>
            <div className={styles.accountStatRow}>
                <Lock size={16} className={styles.accountStatIcon} />
                <div>
                    <div className={styles.accountStatValue}>
                        {user.expiresAt ? formatDate(user.expiresAt) : 'Open-ended'}
                    </div>
                    <div className={styles.accountStatLabel}>Admin access valid until</div>
                </div>
            </div>
            <div className={styles.accountQuickLinks}>
                <Link to="/credits" className={styles.accountQuickLink}>
                    <Ticket size={13} /> Redeem coupon
                </Link>
                <Link to="/analytics" className={styles.accountQuickLink}>
                    <ChartColumn size={13} /> View analytics
                </Link>
                <Link to="/tutorials" className={styles.accountQuickLink}>
                    <GraduationCap size={13} /> Tutorials
                </Link>
            </div>
        </div>
    </div>
);

/* ── Dashboard ────────────────────────────────────────────────────────────── */
const Dashboard = () => {
    const navigate = useNavigate();
    const confirm = useConfirm();
    const { user, refreshUser } = useAuth();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [events, setEvents] = useState([]);
    const [eventsAccessDenied, setEventsAccessDenied] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const { toast, showToast } = useToast();
    const [qrEvent, setQrEvent] = useState(null);
    const [transferEvent, setTransferEvent] = useState(null);
    const [transferRequests, setTransferRequests] = useState({ incoming: [], outgoing: [] });
    const [transferRequestsLoading, setTransferRequestsLoading] = useState(false);
    const [transferAcceptingId, setTransferAcceptingId] = useState(null);
    const [transferDecliningId, setTransferDecliningId] = useState(null);
    const [transferCancelingId, setTransferCancelingId] = useState(null);
    const [exportingEventId, setExportingEventId] = useState(null);
    const [duplicatingEventId, setDuplicatingEventId] = useState(null);
    const [resendVerifLoading, setResendVerifLoading] = useState(false);
    const [eventView, setEventView] = useState(() => {
        try {
            return localStorage.getItem(EVENT_VIEW_STORAGE_KEY) === 'list' ? 'list' : 'grid';
        } catch {
            return 'grid';
        }
    });

    const userName = getUserName();
    const emailVerified = user?.emailVerified ?? true;
    const canCreateEvents = user?.canCreateEvents !== false;

    const coins = user?.coins ?? 0;
    const subEnd = user?.subscriptionEndDate ? new Date(user.subscriptionEndDate) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const hasActiveSubscription = subEnd ? subEnd >= today : false;
    const daysUntilSubExpiry = subEnd ? Math.ceil((subEnd - today) / (24 * 60 * 60 * 1000)) : null;
    const showCouponReminder = user?.role === 'admin' && (
        coins < 50 || (subEnd && subEnd >= today && daysUntilSubExpiry <= 7)
    );

    useEffect(() => {
        try {
            localStorage.setItem(EVENT_VIEW_STORAGE_KEY, eventView);
        } catch {
            // View preference is optional when storage is unavailable.
        }
    }, [eventView]);

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    // SSO signup success: show congratulations once
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (sessionStorage.getItem('showSsoWelcome') === '1') {
            sessionStorage.removeItem('showSsoWelcome');
            showToast('Congratulations! Your account is ready. You can start creating events.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchEvents = async () => {
            setEventsAccessDenied(false);
            try {
                const res = await request(API_URL);
                if (res.ok) {
                    setEvents(await res.json());
                } else if (res.status === 403) {
                    const data = await res.json().catch(() => ({}));
                    if (data.error && data.error.toLowerCase().includes('expired')) {
                        setEventsAccessDenied(true);
                        setEvents([]);
                    } else {
                        showToast(data.error || 'Failed to load events. Please refresh.');
                    }
                } else {
                    const data = await res.json().catch(() => ({}));
                    showToast(data.error || 'Failed to load events. Please refresh.');
                }
            } catch (err) {
                console.error('Failed to fetch events', err);
                showToast('Failed to load events. Please refresh.');
            } finally {
                setIsFetching(false);
            }
        };
        fetchEvents();
    }, []);

    const fetchTransferRequests = useCallback(async () => {
        if (!canCreateEvents) return;
        setTransferRequestsLoading(true);
        try {
            const res = await request(`${API_URL}/transfer-requests`);
            if (res.ok) {
                const data = await res.json();
                setTransferRequests({ incoming: data.incoming || [], outgoing: data.outgoing || [] });
            }
        } catch (err) {
            console.error('Failed to fetch transfer requests', err);
        } finally {
            setTransferRequestsLoading(false);
        }
    }, [canCreateEvents]);

    useEffect(() => {
        fetchTransferRequests();
    }, [fetchTransferRequests]);

    const handleEventCreated = (e) => setEvents(prev => [e, ...prev]);
    const handleEventUpdated = (e) => setEvents(prev => prev.map(x => x.id === e.id ? e : x));

    const handleDelete = async (id) => {
        const ok = await confirm({
            title: 'Delete Event',
            message: 'Delete this event and all its activities?',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setDeletingId(id);
        try {
            const res = await request(`${API_URL}/${id}`, { method: 'DELETE' });
            if (res.ok) {
                setEvents(prev => prev.filter(e => e.id !== id));
                showToast('Event deleted');
            } else {
                const data = await res.json().catch(() => ({}));
                showToast(data.error || 'Failed to delete event');
            }
        } catch (err) {
            console.error('Failed to delete event', err);
            showToast('Failed to delete event');
        } finally {
            setDeletingId(null);
        }
    };

    const handleDuplicate = async (event) => {
        if (!event?.id) return;
        setDuplicatingEventId(event.id);
        try {
            const copyName = (event.name || 'Event').trim().toLowerCase().endsWith(' (copy)')
                ? `${event.name} (2)`
                : `${event.name} (Copy)`;
            const res = await request(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: copyName,
                    startDate: event.start_date,
                    endDate: event.end_date,
                }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                showToast(data.error || 'Failed to duplicate event');
                return;
            }
            const newEvent = await res.json();
            const activitiesRes = await request(`${API_URL}/${event.id}/activities`);
            let failedCount = 0;
            let totalCount = 0;
            let stoppedEarly = false;
            let activitiesFetchFailed = false;
            if (activitiesRes.ok) {
                const activities = await activitiesRes.json();
                const list = Array.isArray(activities) ? activities : [];
                totalCount = list.length;
                const baseUrl = `${API_URL}/${newEvent.id}/activities`;
                for (const activity of list) {
                    const title = activity.title || 'Poll';
                    const createRes = await request(baseUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ type: activity.type, title }),
                    });
                    if (!createRes.ok) {
                        failedCount++;
                        const errData = await createRes.json().catch(() => ({}));
                        if (createRes.status === 402) {
                            showToast(errData.error || 'Insufficient credits to copy all polls.');
                            stoppedEarly = true;
                            break;
                        }
                        continue;
                    }
                    const newActivity = await createRes.json();
                    const content = activity.content && typeof activity.content === 'object'
                        ? JSON.parse(JSON.stringify(activity.content))
                        : null;
                    if (content) {
                        const patchRes = await request(`${baseUrl}/${newActivity.id}`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ content }),
                        });
                        if (!patchRes.ok) console.warn('Failed to copy poll content for', newActivity.id);
                    }
                }
            } else {
                activitiesFetchFailed = true;
            }
            setEvents(prev => [newEvent, ...prev]);
            if (!stoppedEarly) {
                if (activitiesFetchFailed) {
                    showToast('Event duplicated, but its polls could not be copied. Add them manually.');
                } else if (failedCount > 0) {
                    showToast(`Event duplicated; ${totalCount - failedCount} of ${totalCount} poll(s) copied — ${failedCount} failed.`);
                } else {
                    showToast('Event duplicated with all polls!');
                }
            }
        } catch (err) {
            console.error('Failed to duplicate event', err);
            showToast('Failed to duplicate event');
        } finally {
            setDuplicatingEventId(null);
        }
    };

    const handleExportCsv = useCallback(async (event) => {
        if (!event?.id || !event?.code) return;
        setExportingEventId(event.id);
        try {
            const res = await request(`${API_URL}/${event.id}/export`);
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                showToast(data.error || 'Export failed');
                return;
            }
            const data = await res.json();
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 8);
            const filename = `event-${event.code}-${timestamp}.xlsx`;
            exportEventToExcel(data, filename);
            showToast('Export downloaded');
        } catch (err) {
            console.error('Export failed', err);
            showToast('Export failed');
        } finally {
            setExportingEventId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAcceptTransfer = async (eventId) => {
        setTransferAcceptingId(eventId);
        try {
            const res = await request(`${API_URL}/${eventId}/transfer/accept`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setEvents(prev => [...prev, data]);
                fetchTransferRequests();
                showToast('Event transferred to you.');
            } else {
                showToast(data.error || 'Could not accept transfer.');
            }
        } catch (err) {
            showToast('Could not accept transfer.');
        } finally {
            setTransferAcceptingId(null);
        }
    };

    const handleDeclineTransfer = async (eventId) => {
        setTransferDecliningId(eventId);
        try {
            const res = await request(`${API_URL}/${eventId}/transfer/decline`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                fetchTransferRequests();
                showToast('Transfer request declined.');
            } else {
                showToast(data.error || 'Could not decline.');
            }
        } catch (err) {
            showToast('Could not decline.');
        } finally {
            setTransferDecliningId(null);
        }
    };

    const handleCancelTransfer = async (eventId) => {
        setTransferCancelingId(eventId);
        try {
            const res = await request(`${API_URL}/${eventId}/transfer/cancel`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                fetchTransferRequests();
                showToast('Transfer request cancelled. You can send again to the correct email.');
            } else {
                showToast(data.error || 'Could not cancel.');
            }
        } catch (err) {
            showToast('Could not cancel request.');
        } finally {
            setTransferCancelingId(null);
        }
    };

    const handleResendVerification = useCallback(async () => {
        setResendVerifLoading(true);
        try {
            const res = await request(`${API_PATH}/auth/resend-verification`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                showToast(data.message || 'Verification email resent. Please check your inbox.');
            } else {
                showToast(data.error || 'Could not resend verification email.');
            }
        } catch {
            showToast('Could not resend verification email.');
        } finally {
            setResendVerifLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const now = new Date();
    const upcoming = events.filter(e => e.start_date && new Date(e.start_date) > now).length;
    const past = events.filter(e => e.end_date && new Date(e.end_date) < now).length;
    const active = events.filter(e => {
        const start = e.start_date ? new Date(e.start_date) : null;
        const end = e.end_date ? new Date(e.end_date) : null;
        return (!start || now >= start) && (!end || now <= end);
    }).length;

    // Compact "at a glance" preview only — full search/filter/table management
    // lives on the Events page now, so this list never shows more than 3.
    const previewEvents = useMemo(() => {
        const byRecency = [...events].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        const activeFirst = byRecency.filter(e => getEventStatus(e).color === 'active');
        const rest = byRecency.filter(e => getEventStatus(e).color !== 'active');
        return [...activeFirst, ...rest].slice(0, 3);
    }, [events]);

    const pendingTransferIncoming = useMemo(
        () => (transferRequests.incoming || []).filter((r) => r.status === 'pending'),
        [transferRequests.incoming]
    );
    const pendingTransferOutgoing = useMemo(
        () => (transferRequests.outgoing || []).filter((r) => r.status === 'pending'),
        [transferRequests.outgoing]
    );
    const hasPendingTransfers = pendingTransferIncoming.length > 0 || pendingTransferOutgoing.length > 0;

    return (
        <div className={styles.page}>
            {toast && <SimpleToast message={toast} />}
            {qrEvent && (
                <QrModal
                    eventName={qrEvent.name}
                    eventCode={qrEvent.code}
                    joinUrl={`${APP_URL}/join/${qrEvent.code}`}
                    onClose={() => setQrEvent(null)}
                />
            )}
            {transferEvent && (
                <TransferEventModal
                    event={transferEvent}
                    onClose={() => setTransferEvent(null)}
                    onToast={showToast}
                    onTransferRequestCreated={fetchTransferRequests}
                />
            )}

            {/* ── Access banner: email not verified ── */}
            {user && user.role === 'pending' && !emailVerified && (
                <div className={styles.accessBanner}>
                    <AlertCircle size={20} />
                    <div>
                        <p className={styles.accessBannerTitle}>Verify your email to activate your account</p>
                        <p className={styles.accessBannerText}>
                            We&apos;ve sent a verification link to <strong>{user.email}</strong>. Please open your inbox and click
                            &nbsp;<strong>&quot;Verify my email&quot;</strong> so you can start creating events.
                        </p>
                        <p className={styles.accessBannerText}>
                            If you don&apos;t see the email, check your spam folder or click below to get a fresh verification link.
                        </p>
                        <div className={styles.accessBannerActions}>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={handleResendVerification}
                                disabled={resendVerifLoading}
                                icon={resendVerifLoading ? <Loader2 size={14} className={styles.spin} /> : null}
                            >
                                {resendVerifLoading ? 'Sending…' : 'Resend verification email'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Access banner (only expired) ── */}
            {user && !canCreateEvents && user.role === 'admin' && (
                <div className={styles.accessBanner}>
                    <AlertCircle size={20} />
                    <span>
                        Your admin access has expired. You cannot create or access events. Contact superadmin to renew.
                    </span>
                </div>
            )}

            {/* ── Transfer requests: only pending (actionable). Accepted/declined are hidden to keep dashboard focused. ── */}
            {canCreateEvents && hasPendingTransfers && (
                <div className={styles.transferRequestsSection}>
                    <h2 className={styles.transferRequestsTitle}>
                        <Mail size={20} /> Transfer requests
                    </h2>
                    {transferRequestsLoading ? (
                        <p className={styles.transferRequestsEmpty}>Loading…</p>
                    ) : (
                        <>
                            {pendingTransferIncoming.length > 0 && (
                                <div className={styles.transferRequestsBlock}>
                                    <h3 className={styles.transferRequestsSubtitle}>Incoming — accept to receive the event</h3>
                                    <ul className={styles.transferRequestsList}>
                                        {pendingTransferIncoming.map((r) => (
                                            <li key={r.id} className={styles.transferRequestItem}>
                                                <div className={styles.transferRequestInfo}>
                                                    <strong>{r.event_name}</strong>
                                                    <span className={styles.transferRequestMeta}>
                                                        From {r.from_user_name} ({r.from_user_email})
                                                        {r.created_at && (
                                                            <span className={styles.transferRequestDate}>
                                                                {formatDate(r.created_at)}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                                <div className={styles.transferRequestActions}>
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => handleDeclineTransfer(r.event_id)}
                                                        disabled={transferDecliningId === r.event_id}
                                                    >
                                                        {transferDecliningId === r.event_id ? <Loader2 size={14} className={styles.spin} /> : 'Decline'}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleAcceptTransfer(r.event_id)}
                                                        disabled={transferAcceptingId === r.event_id}
                                                        icon={transferAcceptingId === r.event_id ? <Loader2 size={14} className={styles.spin} /> : <CheckCheck size={14} />}
                                                    >
                                                        {transferAcceptingId === r.event_id ? 'Accepting…' : 'Accept'}
                                                    </Button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {pendingTransferOutgoing.length > 0 && (
                                <div className={styles.transferRequestsBlock}>
                                    <h3 className={styles.transferRequestsSubtitle}>Outgoing — event will move when they accept. Wrong email? Cancel and send again.</h3>
                                    <ul className={styles.transferRequestsList}>
                                        {pendingTransferOutgoing.map((r) => (
                                            <li key={r.id} className={styles.transferRequestItem}>
                                                <div className={styles.transferRequestInfo}>
                                                    <strong>{r.event_name}</strong>
                                                    <span className={styles.transferRequestMeta}>
                                                        To {r.to_user_name} ({r.to_user_email})
                                                        <span className={styles.transferRequestPending}>Pending</span>
                                                        {r.created_at && (
                                                            <span className={styles.transferRequestDate}>
                                                                {formatDate(r.created_at)}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() => handleCancelTransfer(r.event_id)}
                                                    disabled={transferCancelingId === r.event_id}
                                                >
                                                    {transferCancelingId === r.event_id ? <Loader2 size={14} className={styles.spin} /> : <X size={14} />}
                                                    Cancel request
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ── Hero / Welcome strip ── */}
            <div className={styles.heroStrip}>
                <div className={styles.heroContent}>
                    <h1 className={styles.greeting}>
                        {getGreeting()}, {userName}! <Sparkles size={20} className={styles.greetingIcon} aria-hidden />
                    </h1>
                    <p className={styles.greetingDate}>
                        {formatDate(new Date())}
                    </p>
                </div>
                <Button
                    className={styles.createBtn}
                    onClick={() => canCreateEvents && setShowCreateModal(true)}
                    icon={<Plus size={18} />}
                    disabled={!canCreateEvents}
                >
                    New Event
                </Button>
            </div>

            {/* ── Admin: low coins or subscription expiring in 7 days — remind to redeem coupon ── */}
            {showCouponReminder && (
                <div className={styles.couponReminderBanner} role="alert">
                    <Ticket size={20} className={styles.couponReminderIcon} aria-hidden />
                    <div className={styles.couponReminderContent}>
                        <strong className={styles.couponReminderTitle}>Apply a coupon soon</strong>
                        <p className={styles.couponReminderText}>
                            {coins < 50 && daysUntilSubExpiry !== null && daysUntilSubExpiry <= 7 && (
                                <>You have <strong>{coins} coins</strong> left and your subscription ends in <strong>{daysUntilSubExpiry} day{daysUntilSubExpiry !== 1 ? 's' : ''}</strong>. Redeem a coupon in <strong>Credits &amp; Coupon</strong> to add coins or extend your subscription and keep creating polls without interruption.</>
                            )}
                            {coins < 50 && (daysUntilSubExpiry === null || daysUntilSubExpiry > 7) && (
                                <>You have <strong>{coins} coins</strong> left (each poll costs 10). Redeem a coupon in <strong>Credits &amp; Coupon</strong> to add credits and continue creating polls.</>
                            )}
                            {coins >= 50 && daysUntilSubExpiry !== null && daysUntilSubExpiry <= 7 && (
                                <>Your subscription expires in <strong>{daysUntilSubExpiry} day{daysUntilSubExpiry !== 1 ? 's' : ''}</strong>. Redeem a coupon in <strong>Credits &amp; Coupon</strong> to extend it and keep unlimited poll creation.</>
                            )}
                        </p>
                        <Link to="/credits" className={styles.couponReminderLink}>Go to Credits &amp; Coupon → Redeem coupon</Link>
                    </div>
                </div>
            )}

            {/* ── Stats Row ── */}
            <div className={styles.statsRow}>
                <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.statIconPrimary}`}>
                        <CalendarRange size={20} />
                    </div>
                    <div className={styles.statBody}>
                        <div className={styles.statValue}>{isFetching ? '—' : events.length}</div>
                        <div className={styles.statLabel}>Total Events</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.statIconWarning}`}>
                        <Clock size={20} />
                    </div>
                    <div className={styles.statBody}>
                        <div className={styles.statValue}>{isFetching ? '—' : upcoming}</div>
                        <div className={styles.statLabel}>Upcoming</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.statIconSuccess}`}>
                        <Zap size={20} />
                    </div>
                    <div className={styles.statBody}>
                        <div className={styles.statValue}>{isFetching ? '—' : active}</div>
                        <div className={styles.statLabel}>Active Now</div>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={`${styles.statIcon} ${styles.statIconNeutral}`}>
                        <Clock size={20} />
                    </div>
                    <div className={styles.statBody}>
                        <div className={styles.statValue}>{isFetching ? '—' : past}</div>
                        <div className={styles.statLabel}>Past</div>
                    </div>
                </div>
            </div>

            {/* ── Overview: charts + account/quick-actions side by side ── */}
            {!isFetching && events.length > 0 && (
                <section className={styles.overviewSection} aria-label="Overview">
                    <h2 className={styles.overviewTitle}>Overview</h2>
                    <div className={`${styles.overviewGrid} ${events.length > 0 && user?.role === 'admin' ? styles.overviewGridWithAccount : ''}`}>
                        {events.length > 0 && (
                            <div className={styles.overviewCharts}>
                                <DashboardCharts events={events} />
                            </div>
                        )}
                        {events.length > 0 && user?.role === 'admin' && (
                            <AccountQuickActions user={user} coins={coins} hasActiveSubscription={hasActiveSubscription} />
                        )}
                    </div>
                </section>
            )}

            <div className={styles.dashboardLowerGrid}>
                {user?.role === 'admin' && events.length === 0 && (
                    <AccountQuickActions user={user} coins={coins} hasActiveSubscription={hasActiveSubscription} />
                )}

                {/* ── Events preview: compact, links to the full Events page ── */}
                <section className={styles.eventsPreviewSection} aria-labelledby="dashboard-events-heading">
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionLeft}>
                            <h2 id="dashboard-events-heading" className={styles.sectionTitle}>
                                {canCreateEvents ? 'Active now' : 'Your Events'}
                            </h2>
                        </div>
                        <div className={styles.sectionHeaderActions}>
                            {canCreateEvents && previewEvents.length > 0 && (
                                <div className={styles.viewToggle} role="group" aria-label="Event view">
                                    <button
                                        type="button"
                                        className={`${styles.viewToggleButton} ${eventView === 'grid' ? styles.viewToggleButtonActive : ''}`}
                                        onClick={() => setEventView('grid')}
                                        aria-label="Show events as cards"
                                        aria-pressed={eventView === 'grid'}
                                    >
                                        <Grid2X2 size={15} aria-hidden />
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.viewToggleButton} ${eventView === 'list' ? styles.viewToggleButtonActive : ''}`}
                                        onClick={() => setEventView('list')}
                                        aria-label="Show events as a list"
                                        aria-pressed={eventView === 'list'}
                                    >
                                        <List size={16} aria-hidden />
                                    </button>
                                </div>
                            )}
                            {canCreateEvents && events.length > 0 && (
                                <Link to="/events" className={styles.viewAllLink}>
                                    View all events <ArrowRight size={14} />
                                </Link>
                            )}
                        </div>
                    </div>

                    {isFetching ? (
                        <div className={styles.eventsGrid}>
                            {[1, 2, 3].map(i => (
                                <div key={i} className={styles.skeletonCard}>
                                    <div className={styles.skeletonAccent} />
                                    <div className={styles.skeletonBody}>
                                        <div className={styles.shimmer} style={{ height: 18, width: '65%' }} />
                                        <div className={styles.shimmer} style={{ height: 13, width: '40%', marginTop: 8 }} />
                                        <div className={styles.shimmer} style={{ height: 32, width: '55%', marginTop: 12 }} />
                                    </div>
                                    <div className={styles.skeletonFooter}>
                                        <div className={styles.shimmer} style={{ height: 30, flex: 1 }} />
                                        <div className={styles.shimmer} style={{ height: 30, flex: 1 }} />
                                        <div className={styles.shimmer} style={{ height: 30, flex: 1 }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : !canCreateEvents ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIconWrap}>
                                <Lock size={32} className={styles.emptyIconGlyph} aria-hidden />
                            </div>
                            <h3 className={styles.emptyTitle}>
                                {user?.role === 'pending' ? 'Access pending' : 'Access expired'}
                            </h3>
                            <p className={styles.emptyDesc}>
                                {user?.role === 'pending'
                                    ? 'You cannot view or manage events until a email verification.'
                                    : 'You cannot view or manage your events until your access is renewed. Contact your superadmin.'}
                            </p>
                        </div>
                    ) : previewEvents.length === 0 ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIconWrap}>
                                <Target size={32} className={styles.emptyIconGlyph} aria-hidden />
                            </div>
                            <h3 className={styles.emptyTitle}>No events yet</h3>
                            <p className={styles.emptyDesc}>
                                Create your first event to start engaging your audience with live polls, Q&amp;A, and more.
                            </p>
                        </div>
                    ) : (
                        <div className={`${styles.eventsGrid} ${eventView === 'list' ? styles.eventsList : ''}`}>
                            {previewEvents.map(event => (
                                <EventCard
                                    key={event.id}
                                    event={event}
                                    view={eventView}
                                    onEdit={canCreateEvents ? setEditingEvent : undefined}
                                    onDelete={canCreateEvents ? handleDelete : undefined}
                                    onDuplicate={canCreateEvents ? handleDuplicate : undefined}
                                    onTransfer={canCreateEvents ? setTransferEvent : undefined}
                                    onExportCsv={canCreateEvents && event.hasResponses ? handleExportCsv : undefined}
                                    onQr={canCreateEvents ? setQrEvent : undefined}
                                    onToast={showToast}
                                    deletingId={deletingId}
                                    exportingEventId={exportingEventId}
                                    duplicatingEventId={duplicatingEventId}
                                    onClick={() => canCreateEvents && navigate(`/events/${event.id}`)}
                                />
                            ))}
                        </div>
                    )}
                </section>
            </div>

            <CreateEventModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreateEvent={handleEventCreated}
            />
            <EditEventModal
                isOpen={!!editingEvent}
                event={editingEvent}
                onClose={() => setEditingEvent(null)}
                onUpdated={handleEventUpdated}
            />
        </div>
    );
};

export default Dashboard;
